import { Component, ChangeDetectorRef, HostListener } from '@angular/core';
import { MatDialogRef } from '@angular/material/dialog';
import { HelperService } from '@app/services/helper.service';
import { LoggingService, LogLevel } from '@app/services/logging.service';

@Component({
  selector: 'app-input-otp-dialog',
  templateUrl: './input-otp-dialog.component.html',
  styleUrls: ['./input-otp-dialog.component.scss'],
  standalone: false
})
export class InputOtpDialogComponent {
  static readonly COMPONENT_NAME = 'InputOtpDialogComponent';

  otpDigits: string[] = Array(6).fill('');
  currentIndex = 0;

  constructor(
    public dialogRef: MatDialogRef<InputOtpDialogComponent>,
    private cdr: ChangeDetectorRef,
    private helper: HelperService,
    private loggingService: LoggingService
  ) { }

  // Global Enter handler to submit OTP from anywhere in the dialog
  @HostListener('document:keydown.enter', ['$event'])
  onGlobalEnter(event: KeyboardEvent) {
    // Let the form/dialog handle this once
    event.preventDefault();
    event.stopPropagation();
    this.onSubmit();
  }

  onKeyDown(event: KeyboardEvent, i: number) {
    this.loggingService.log(LogLevel.DEBUG, InputOtpDialogComponent.COMPONENT_NAME, 'On Key Down:', event, 'i:', i);

    const key = event.key;

    // Allow common ctrl/meta combos (copy/paste/cut/select all)
    if ((event.ctrlKey || event.metaKey) && ['a','c','v','x','A','C','V','X'].includes(key)) {
      return; // do not prevent default; paste handler will manage content
    }

    // Let navigation keys work normally
    if (["ArrowLeft", "ArrowRight", "Tab"].includes(key)) {
      return;
    }

    // Prevent default for the rest so we control the input behavior
    event.preventDefault();

    if (/^[0-9]$/.test(key)) {
      this.otpDigits[i] = key;
    } else if (key === 'Backspace') {
      this.otpDigits[i] = '';
      if (i > 0) this.focusInput(i - 1);
    } else if (key === 'Enter') {
      this.onSubmit();
    }
  }

  onPaste(event: ClipboardEvent, i: number) {
    this.loggingService.log(LogLevel.DEBUG, InputOtpDialogComponent.COMPONENT_NAME, 'On Paste:', event);

    event.preventDefault();
    const paste = event.clipboardData?.getData('text') || '';
    const digits = paste.replace(/[^0-9]/g, '').slice(0, 6);
    // Clear all boxes first
    this.otpDigits = Array(6).fill('');
    for (let j = 0; j < digits.length; j++) {
      this.otpDigits[j] = digits[j];
    }
    this.cdr.detectChanges();
    if (digits.length >= 6) {
      this.focusVerifyButton();
    } else {
      this.focusInput(Math.min(digits.length, 5));
    }
  }

  onFocus(i: number) {
    this.currentIndex = i;
  }

  focusInput(i: number) {
    const input = document.getElementById(`otp-input-${i}`) as HTMLInputElement;
    if (input) input.focus();
    this.currentIndex = i;
  }

  private focusVerifyButton() {
    const btn = document.getElementById('otp-verify-btn') as HTMLButtonElement;
    if (btn) btn.focus();
    this.currentIndex = 5;
  }

  get isOtpComplete() {
    return this.otpDigits.every(d => d !== '');
  }

  onSubmit() {
    if (this.isOtpComplete) {
      this.dialogRef.close({ otp: this.otpDigits.join('') });
    }
  }

  onCancel() {
    this.dialogRef.close();
  }
}